import * as React from "react";
import styled, { css } from "styled-components";
import { FormGroup } from "components/bootstrap";
import { InputDescription, Select, useChipInput } from "components/common";
import commonStyles from "components/event-definitions/common/commonStyles.css";
import useLicenseCheck from "license/useLicenseCheck";
import useMitreNameIndex, {
  formatMitreCategory
} from "security-app/components/SecurityEvents/Alerts/MitreCategories/useMitreNameIndex";
import useMitreFlatOptions from "security-app/components/SecurityEvents/Alerts/MitreCategories/useMitreFlatOptions";
const MAX_TACTICS_TECHNIQUES = 64;
const VALID_PATTERN = /^(TA\d{4}|T\d{4}(\.\d{3})?)$/;
const quote = (s) => `"${s}"`;
const buildInvalidMessage = (invalidList) => {
  if (invalidList.length === 0) return null;
  const isPlural = invalidList.length > 1;
  return `ID${isPlural ? "s" : ""} ${invalidList.map(quote).join(", ")} ${isPlural ? "are" : "is"} not valid. Expected format: TA0000, T0000, or T0000.000.`;
};
const HELP_TEXT = "Press Enter or Tab to add. Accepts tactic IDs (TA0000), technique IDs (T0000), and sub-technique IDs (T0000.000).";
const HELP_TEXT_WITH_CATALOGUE = "Pick from the installed MITRE catalogue, or type an ID and press Enter or Tab. Accepts tactic IDs (TA0000), technique IDs (T0000), and sub-technique IDs (T0000.000).";
const normalizeId = (raw) => raw.trim().toUpperCase();
const validateId = (id) => VALID_PATTERN.test(id) ? null : buildInvalidMessage([id]);
const matchesCatalogue = (options, raw) => {
  const query = raw.trim().toLowerCase();
  return query.length > 0 && options.some((option) => option.label.toLowerCase().includes(query));
};
const OptionRow = styled.div(
  ({ $depth }) => css`
    display: flex;
    align-items: baseline;
    padding-left: ${$depth * 16}px;
  `
);
const OptionLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const ChipLabel = styled.span`
  display: inline-block;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
`;
const Editor = ({ value, onChange, disabled = false, error = null }) => {
  const { nameById } = useMitreNameIndex();
  const { options } = useMitreFlatOptions();
  const hasCatalogue = options.length > 0;
  const { input, messages, isAtMax, setDuplicateAttempt, flagDuplicateAttempt, selectProps, reactSelectProps } = useChipInput({
    values: value,
    onChange,
    normalize: normalizeId,
    validate: validateId,
    max: MAX_TACTICS_TECHNIQUES,
    describeInvalid: buildInvalidMessage,
    describeDuplicate: (id) => `"${id}" is already added.`,
    // The input doubles as a catalogue search, so text appearing in a row is a query, not a bad ID.
    validateInput: (candidate, raw) => matchesCatalogue(options, raw) ? null : validateId(candidate)
  });
  const isSearching = input.trim().length > 0;
  const renderOption = (option) => {
    if (option.name === void 0) return /* @__PURE__ */ React.createElement(OptionRow, { $depth: 0 }, option.label);
    const text = `${isSearching ? option.breadcrumb : option.name} (${option.value})`;
    return /* @__PURE__ */ React.createElement(OptionRow, { $depth: isSearching ? 0 : option.depth }, /* @__PURE__ */ React.createElement(OptionLabel, { title: text }, text));
  };
  const renderChip = ({ value: id }) => {
    const label = formatMitreCategory(id, nameById);
    return /* @__PURE__ */ React.createElement(ChipLabel, { title: label }, label);
  };
  const selectableOptions = options.map((option) => ({
    ...option,
    disabled: isAtMax || value.includes(option.value)
  }));
  const errorMessages = [
    error ? String(error) : null,
    ...messages,
    isAtMax ? `Maximum of ${MAX_TACTICS_TECHNIQUES} entries reached.` : null
  ].filter(Boolean);
  return /* @__PURE__ */ React.createElement(FormGroup, { validationState: errorMessages.length > 0 ? "error" : null }, /* @__PURE__ */ React.createElement(
    Select,
    {
      key: hasCatalogue ? "catalogue" : "bare",
      ...selectProps,
      ...reactSelectProps,
      ...{
        // A typed ID fuzzy-matches rows it isn't (T1685 matches T1608.005) and Enter takes the
        // first row, so the Create row has to lead. It only appears when the input is a valid
        // ID that isn't already added.
        createOptionPosition: "first",
        onKeyDown: (e) => {
          if (e.key !== "Enter" && e.key !== "Tab") return;
          const normalized = normalizeId(input);
          if (normalized && value.includes(normalized)) {
            setDuplicateAttempt(normalized);
            e.preventDefault();
            return;
          }
          flagDuplicateAttempt();
        }
      },
      inputId: "tactics-techniques",
      multi: true,
      allowCreate: true,
      options: selectableOptions,
      optionRenderer: renderOption,
      valueRenderer: renderChip,
      placeholder: "Add tactic/technique IDs (e.g. TA0002, T1059, T1059.001)",
      disabled,
      "aria-label": "Tactics/Techniques"
    }
  ), /* @__PURE__ */ React.createElement(
    InputDescription,
    {
      help: hasCatalogue ? HELP_TEXT_WITH_CATALOGUE : HELP_TEXT,
      error: errorMessages.length > 0 ? errorMessages.join(" ") : void 0
    }
  ));
};
const TacticsTechniquesEditorPlugin = ({ value, onChange, disabled = false, error = null }) => {
  const {
    security: { isValid: isValidSecurityLicense }
  } = useLicenseCheck();
  if (!isValidSecurityLicense) return null;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("h2", { className: commonStyles.title }, "Tactics/Techniques ", /* @__PURE__ */ React.createElement("small", null, "(optional)")), /* @__PURE__ */ React.createElement("p", null, "Tag this Event Definition with MITRE ATT&CK\xAE tactic, technique, or sub-technique IDs."), /* @__PURE__ */ React.createElement(Editor, { value, onChange, disabled, error }));
};
export default TacticsTechniquesEditorPlugin;
